import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  adminApi,
  adminAuth,
  type AdminOrder,
  type FollowUp,
  type StatsResponse,
} from '../lib/adminApi';

/**
 * The new Dashboard is a "morning overview" page rather than the orders
 * table. Layout (3 sections):
 *   1. Stats grid – totals + revenue
 *   2. Today's tasks – what needs action today
 *   3. Recent filings – latest activity at a glance
 *
 * The full orders table lives at /admin/filings and the full tasks list
 * at /admin/tasks. Sidebar nav gets you there in one click.
 */
export function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [todayTasks, setTodayTasks] = useState<FollowUp[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<FollowUp[]>([]);
  const [recentOrders, setRecentOrders] = useState<AdminOrder[]>([]);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    adminApi.stats().then(setStats).catch((e) => setError(String(e.message)));
    adminApi.listFollowUps('today').then((r) => setTodayTasks(r.items)).catch(() => {});
    adminApi.listFollowUps('upcoming').then((r) => setUpcomingTasks(r.items)).catch(() => {});
    adminApi.listOrders({ pageSize: 5 }).then((r) => setRecentOrders(r.items)).catch(() => {});
  }

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    refresh();
  }, [navigate]);

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Today at a glance.</p>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {stats && <StatsBar stats={stats} />}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* ─── Today's tasks (full-width on mobile, 1 col on desktop) ─── */}
        <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <SectionHeader title="Today's tasks" count={todayTasks.length} viewAllTo="/admin/tasks" />
          {todayTasks.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No tasks for today. Enjoy.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {todayTasks.slice(0, 6).map((t) => (
                <TaskRow key={t.id} task={t} onDone={refresh} />
              ))}
            </ul>
          )}
        </section>

        {/* ─── Upcoming tasks ─── */}
        <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <SectionHeader title="Upcoming (30 days)" count={upcomingTasks.length} viewAllTo="/admin/tasks" />
          {upcomingTasks.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Nothing scheduled.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {upcomingTasks.slice(0, 6).map((t) => (
                <TaskRow key={t.id} task={t} onDone={refresh} />
              ))}
            </ul>
          )}
        </section>

        {/* ─── Recent filings ─── */}
        <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <SectionHeader title="Recent filings" count={recentOrders.length} viewAllTo="/admin/filings" />
          {recentOrders.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No filings yet.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {recentOrders.map((o) => (
                <RecentOrderRow key={o.id} order={o} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

function SectionHeader({ title, count, viewAllTo }: { title: string; count: number; viewAllTo: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-400">{count}</span>
        <Link to={viewAllTo} className="text-brand-700 hover:underline">
          View all →
        </Link>
      </div>
    </div>
  );
}

function TaskRow({ task, onDone }: { task: FollowUp; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const overdue = task.dueDate < new Date().toISOString().slice(0, 10);
  return (
    <li className="text-xs">
      <div className="flex items-start gap-2">
        <span
          className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
            overdue ? 'bg-red-500' : 'bg-brand-500'
          }`}
        />
        <div className="min-w-0 flex-1">
          <Link to={`/admin/orders/${task.orderId}`} className="block truncate font-medium text-slate-900 hover:text-brand-700">
            {task.companyName}
          </Link>
          <p className="mt-0.5 line-clamp-2 text-slate-600">{task.reason}</p>
          <div className="mt-1 flex items-center justify-between">
            <span className={`font-medium ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
              {relativeDateLabel(task.dueDate)}
            </span>
            <button
              onClick={async () => {
                setBusy(true);
                await adminApi.updateFollowUp(task.id, { status: 'COMPLETED' });
                onDone();
                setBusy(false);
              }}
              disabled={busy}
              className="text-[11px] text-brand-700 hover:underline disabled:text-slate-400"
            >
              {busy ? '…' : 'Mark done'}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

function RecentOrderRow({ order }: { order: AdminOrder }) {
  const tone = STATUS_TONE[order.status] || 'bg-slate-100 text-slate-700';
  return (
    <li className="text-xs">
      <Link to={`/admin/orders/${order.id}`} className="block hover:opacity-90">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-slate-900">{order.companyName}</div>
            <div className="mt-0.5 truncate text-slate-500">{order.email}</div>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>
            {order.status}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
          <span>£{(order.amountPence / 100).toFixed(2)}</span>
          <span>
            {new Date(order.createdAt).toLocaleDateString('en-GB', {
              day: 'numeric', month: 'short',
            })}
          </span>
        </div>
      </Link>
    </li>
  );
}

const STATUS_TONE: Record<string, string> = {
  PENDING_PAYMENT: 'bg-amber-100 text-amber-700',
  PAID: 'bg-blue-100 text-blue-700',
  SUBMITTING: 'bg-indigo-100 text-indigo-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

function StatsBar({ stats }: { stats: StatsResponse }) {
  const counts = stats.countsByStatus;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <Card label="Total filings" value={total.toLocaleString('en-GB')} />
      <Card label="Accepted" value={(counts.ACCEPTED || 0).toLocaleString('en-GB')} tone="green" />
      <Card label="Rejected" value={(counts.REJECTED || 0).toLocaleString('en-GB')} tone="red" />
      <Card label="Revenue 7d" value={`£${(stats.revenue.last7dPence / 100).toFixed(2)}`} />
      <Card label="Revenue 30d" value={`£${(stats.revenue.last30dPence / 100).toFixed(2)}`} />
    </div>
  );
}

function Card({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'red' }) {
  const valueColor = tone === 'green' ? 'text-green-700' : tone === 'red' ? 'text-red-700' : 'text-slate-900';
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${valueColor}`}>{value}</div>
    </div>
  );
}

function relativeDateLabel(iso: string): string {
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z');
  const d = new Date(iso + 'T00:00:00Z');
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (days < -1) return `${-days} days overdue`;
  if (days === -1) return 'Overdue';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}
